import type { PropsWithChildren } from 'react';

import { createContext, useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { authClient } from '@/services/auth';
import { ensureUserDetails } from '@/services/webservices/auth';

type UserLoginProps = {
	name: string;
	username: string;
	role: string;
	subAgentNumber: string;
	underwriterNumber: string;
};

type OktaUser = {
	createdAt: Date | null;
	email: string;
	emailVerified: boolean;
	id: string;
	name: string;
	updatedAt: Date | null;
};

type OktaSession = {
	createdAt: Date;
	expiresAt: Date;
	id: string;
	ipAddress: string;
	token: string;
	updatedAt: Date;
	userAgent: string;
	userId: string;
};

type AuthProviderProps = PropsWithChildren;
type AuthContextProps = {
	isLoggedIn: boolean;
	logIn: () => Promise<void>;
	logOut: () => Promise<void>;
	user: UserLoginProps & OktaUser;
};

const defaultOktaUser = {
	createdAt: null,
	email: '',
	emailVerified: false,
	id: '',
	name: '',
	updatedAt: null
};

const defaultUserObject = {
	username: '',
	name: '',
	role: '',
	subAgentNumber: '',
	underwriterNumber: ''
};

const fakeLoginObject: AgentTokenInformation = {
	identityProvider: 'okta',
	firstName: 'Test',
	lastName: 'Testington',
	role: 'AGENT',
	subAgentNumber: '1',
	underwriterNumber: '123',
	username: 'test@test.test'
};

const AuthContext = createContext<AuthContextProps | undefined>( undefined );

function AuthProvider({ children }: AuthProviderProps ) {
	const [ userDetails, setUserDetails ] = useState<AgentTokenInformation | undefined>( undefined );

	const router = useRouter();

	const { data: session } = authClient.useSession();

	const logIn = async () => {
		await authClient.signIn.social({
			provider: 'okta',
			callbackURL: '/dashboard'
		});
	};

	const logOut = async () => {
		await authClient.signOut({
			fetchOptions: {
				onSuccess: () => {
					router.push( '/sign-in' )
				}
			}
		});
	};

	useEffect( () => {
		async function handleLogin() {
			const userInfo = await ensureUserDetails( session?.user?.name ?? '' );

			setUserDetails( userInfo );
		}
	
		if ( !session?.session?.token || userDetails !== undefined ) {
			return;
		}

		handleLogin();
	}, [ session, userDetails, setUserDetails ] );

	return (
		<AuthContext.Provider value={{
			isLoggedIn: Boolean( session?.user?.id ) && Boolean( userDetails ),
			logIn,
			logOut,
			user: {
				...( userDetails || defaultUserObject ),
				...( session?.user || defaultOktaUser )
			}
		}}>
			{ children }
		</AuthContext.Provider>
	);
}

function useAuth() {
	const context = useContext( AuthContext );

	if ( context === undefined ) {
		throw new Error( 'useAuth must be used within a AuthProvider' );
	}

	return context;
}

export { AuthProvider, useAuth };
